/**
 * AI Orchestrator — public facade (Phase 4, final before OpenAI).
 *
 * Coordination only. The two entry points:
 *
 *   await generateFromPrompt({ prompt: 'grand opening flyer for a burger place' })
 *   //  → { brief, direction, plan, templates (with commands), stats }
 *   //    runs prompt → provider(mock) → Director → Factory → Engine, offline
 *
 *   await generateBatch({ total: 100 })
 *   //  → production batches: 10/20/50/100/500/1000, chunked, cancellable
 *
 * Registered providers: mock (ready) + OpenAI/Claude/Gemini/Local stubs
 * (interface-only). When a real key exists, implementing ONE adapter's
 * generate() is the only code required — see README.md.
 */

import { registerProvider } from './providerInterface';
import { mockProvider } from './mockProvider';
import { OpenAIAdapter } from './adapters/openaiAdapter';
import { ClaudeAdapter } from './adapters/claudeAdapter';
import { GeminiAdapter } from './adapters/geminiAdapter';
import { LocalLLMAdapter } from './adapters/localLLMAdapter';

// Register the offline mock plus the four future adapters so the Playground
// can list them (disabled until isReady() flips true).
registerProvider(mockProvider);
registerProvider(new OpenAIAdapter());
registerProvider(new ClaudeAdapter());
registerProvider(new GeminiAdapter());
registerProvider(new LocalLLMAdapter());

export { generateFromPrompt } from './orchestrator';
export { generateBatch, BATCH_SIZES } from './batchGenerator';
export {
  AIProvider, NotImplementedError,
  registerProvider, getProvider, listProviders,
} from './providerInterface';
export { MockProvider, mockProvider, hashString } from './mockProvider';
export { buildCreativeBriefPrompt } from './promptBuilder';
export {
  buildCreativeBriefFunction, parseCreativeBriefCall, CREATIVE_BRIEF_FUNCTION_NAME,
} from './functionSchema';
export { ConversationContext } from './conversationContext';
export { StreamHandle, CHUNK_TYPES, createChunk } from './streamingInterface';
export { withRetry, DEFAULT_RETRY_OPTIONS } from './retryManager';
export { CancellationToken, CancelledError } from './cancellation';
export { STAGES, STAGE_ORDER, createProgressEmitter } from './progressEvents';
export { ImageProvider, MockImageProvider, mockImageProvider } from './imageProviderInterface';
export { OpenAIAdapter } from './adapters/openaiAdapter';
export { ClaudeAdapter } from './adapters/claudeAdapter';
export { GeminiAdapter } from './adapters/geminiAdapter';
export { LocalLLMAdapter } from './adapters/localLLMAdapter';
